import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo, useState, useRef, useCallback, useGridCellEditor } from 'react';
import { useContactService } from '../services/contactService';
import { useAccessRequestService } from '../services/accessRequestService';
import { useFirmDomainService } from '../services/firmDomainService';
import LoadingModal from '../components/LoadingModal';
import StatusModal from "../components/StatusModal";
import NoAccess from '../components/NoAccess';
import { Trash } from 'react-bootstrap-icons';

const RequestPage = () => {
    const gridRef = useRef(null); // Reference to the grid
    const [rowData, setRowData] = useState();
    const [originalData, setOriginalData] = useState();
    const [firmDomains, setFirmDomains] = useState();
    const [statusMessage, setStatusMessage] = useState("");
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusSuccess, setStatusSuccess] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [showNoAccess, setshowNoAccess] = useState(false);
    const [shouldEdit, setShouldEdit] = useState(false);
    const [newRowId, setnewRowId] = useState(false);

    const navigate = useNavigate();

    const contactService = useContactService();
    const accessRequestService = useAccessRequestService();
    const firmDomainService = useFirmDomainService();

    useEffect(() => {
        setShowLoadingModal(true);
        setShowStatusModal(false);
        setStatusMessage("");
        getContacts();
        getFirmDomains();
    }, []);

    const getContacts = () => {
        contactService.getContactsByFirm()
            .then((data) => {
                if (data.length === 0) {
                    setshowNoAccess(true);
                }
                addIds(data);
                setnewRowId(data.length);
                addValidation(data);
                setRowData(data);
                setOriginalData(structuredClone(data));
                setShowLoadingModal(false);
            })
    }

    const getFirmDomains = () => {
        firmDomainService.getEmailDomains()
            .then((data) => {
                setFirmDomains(data);
            })
    }

    const defaultColDef = useMemo(() => {
        return {
            flex: 1,
            minWidth: 200,
            sortable: true,
        };
    }, []);

    const columnDefs = [
        {
            headerName: "First Name", field: "firstName",
            cellRenderer: (props) => {
                if (props.value !== undefined) {
                    return props.value;
                } else {
                    return (
                        <img src="https://www.ag-grid.com/example-assets/loading.gif" />
                    );
                }
            },
            editable: (params) => {
                return params.data.isEditable;
            },
            cellClass: (params) => {
                if (params.data.isInvalid.includes("firstName")) {
                    return "cell-error";
                }
                return null;
            },
            tooltipValueGetter: (params) => {
                if (params.data.isInvalid.includes("firstName")) {
                    return "First name is required.";
                }
                return null;
            },
        },
        {
            headerName: "Last Name",
            field: "lastName",
            editable: (params) => {
                return params.data.isEditable;
            },
            cellClass: (params) => {
                if (params.data.isInvalid.includes("lastName")) {
                    return "cell-error";
                }
                return null;
            },
            tooltipValueGetter: (params) => {
                if (params.data.isInvalid.includes("lastName")) {
                    return "Last name is required.";
                }
                return null;
            },
        },
        {
            headerName: "Email",
            field: "email",
            editable: (params) => {
                return params.data.isEditable;
            },
            cellClass: (params) => {
                if (params.data.isInvalid.includes("email")) {
                    return "cell-error";
                }
                return null;
            },
            tooltipValueGetter: (params) => {
                if (params.data.isInvalid.includes("email")) {
                    return "Email is required and must be a firm email address. (" + firmDomains.join(" or ") + ")";
                }
                return null;
            },
        },
        {
            headerName: "DataView CMB Access",
            field: "access",
            editable: true,
            cellRenderer: 'agCheckboxCellRenderer',
        },
        {
            headerName: "", field: "isEditable", maxWidth: 78, sortable: false,
            cellRenderer: (props) => {
                if (props.value !== undefined) {
                    return (
                        <button className="btn btn-danger bi bi-trash" onClick={() => deleteRow(props.data.id)}><Trash size={20} /></button>
                    );
                }
            },
        },
    ];

    function addValidation(data) {
        data.map(d => d.isInvalid = []);
    }

    function addIds(data) {
        let count = 0;
        data.map(d => d.id = count++);
    }

    function createNewRow() {
        const newRow = {
            "id": newRowId,
            "firstName": "",
            "lastName": "",
            "email": "",
            "access": true,
            "isEditable": true,
            "isInvalid": []
        };
        setnewRowId(newRowId + 1);
        return newRow;
    }

    const addUser = () => {
        const newRow = createNewRow();
        const updatedRowData = [...rowData, newRow];
        setRowData(updatedRowData);
        setShouldEdit(true);
    };

    const deleteRow = (id) => {
        const rowIndex = rowData.findIndex(row => row.id === id);
        rowData.splice(rowIndex, 1);
        const updatedRowData = [...rowData];
        setRowData(updatedRowData);
        setTimeout(() => {
            navigateToNode(rowData.length - 1);
        }, 0);
    }

    const onRowDataUpdated = useCallback(
        (event) => {
            if (shouldEdit) {
                const rowIndex = rowData.length - 1;

                if (gridRef.current.api) {
                    navigateToNode(rowIndex);
                    const node = gridRef.current.api.getRowNode(rowIndex);
                    setTimeout(() => {
                        gridRef.current.api.startEditingCell({
                            rowIndex: node.childIndex,
                            colKey: 'firstName',
                        });
                    }, 0);
                }
                setShouldEdit(false);
            }
        },
        [shouldEdit]
    );

    const navigateToNode = (rowIndex) => {
        const node = gridRef.current.api.getRowNode(rowIndex);
        gridRef.current.api.ensureNodeVisible(node, 'middle');
    }

    const submitRequest = async () => {
        if (gridRef.current.api) {
            gridRef.current.api.stopEditing(); // Ends cell editing to prevent submit click ignore.
        }

        setShowLoadingModal(true);

        let newAccessRequests = [];
        for (let i = 0; i < originalData.length; i++) {
            if (originalData[i].access !== rowData[i].access) {
                newAccessRequests.push(rowData[i]);
            }
        }

        //Add new rows after validation
        for (let i = originalData.length; i < rowData.length; i++) {
            validate(rowData[i]);
            if (rowData[i].isInvalid.length !== 0) {
                gridRef.current.api.redrawRows();
                setShowLoadingModal(false);
                return null;
            }
            else {
                newAccessRequests.push(rowData[i]);
            }
        }

        await accessRequestService.createRequest({ newAccessRequests })
            .then((response) => {
                if (response) {
                    navigate("/success");
                }
                else {
                    setShowStatusModal(true);
                    setStatusMessage("Something went wrong. Please try again later or contact membersupport@alas.com.");
                    setStatusSuccess(false);
                    setShowLoadingModal(false);
                }
            })
    }

    const handleStatusModalClose = () => {
        setShowStatusModal(false);
    }

    const onRowEditingStopped = (params) => {
        validate(params.data);
    }

    const validate = (data) => {
        if (data.isEditable) {

            data.isInvalid = [];

            if (data.firstName == null || data.firstName.length === 0) {
                data.isInvalid.push("firstName");
            }

            if (data.lastName == null || data.lastName.length === 0) {
                data.isInvalid.push("lastName");
            }

            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

            if (data.email == null || data.email.length === 0 || !emailRegex.test(data.email) || !firmDomains.includes(data.email.split('@').pop())) {
                data.isInvalid.push("email");
            }

            const refreshCellParams = {
                force: true,
                suppressFlash: true,
            };
            gridRef.current.api.refreshCells(refreshCellParams);
        }
    }

    return (
        <div>
            {
                (showNoAccess) ?
                <>
                    <NoAccess />
                </> :
                <>
                    <div class="top-text">
                            To update your firm&rsquo;s access to DataView&#8482; CMB, our interactive online resource that provides member firms with close to real-time data about claims and reported matters as well as their firm&rsquo;s risk profile, and benchmarking data, please select the contacts listed below that should have access. Please allow up to one business day for ALAS personnel to review and process your request.
                    </div>
                    <div className="ag-theme-quartz" style={{ height: '600px', width: '100%' }}>
                        <LoadingModal show={showLoadingModal} />
                        <StatusModal showStatusModal={showStatusModal} statusMessage={statusMessage}
                            statusSuccess={statusSuccess}
                            handleStatusModalClose={handleStatusModalClose} />
                        <AgGridReact
                            ref={gridRef}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            rowData={rowData}
                            editType={"fullRow"}
                            singleClickEdit={true}
                            onRowDataUpdated={onRowDataUpdated}
                            onRowEditingStopped={onRowEditingStopped}
                            tooltipShowDelay={1000}
                        />
                    </div>

                    <div class="row justify-content-between">
                        <div class="col-auto">
                            <button type="button" class="btn btn-primary" onClick={addUser}>Add User</button>
                        </div>
                        <div class="col-auto">
                            <button type="button" class="btn btn-primary" onClick={submitRequest}>Submit</button>
                        </div>
                    </div>
                </>
            }
        </div>
    );
};

export default RequestPage;